import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sanitizeImageUrl } from '../utils/api';
import DiscordIcon from './DiscordIcon';
import ArrowIcon from './ArrowIcon';

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const NAV_ITEMS = [
  { page: 'dashboard', icon: '🏠', labelKey: 'nav_overview' },
  { page: 'clan', icon: '🏰', labelKey: 'nav_clan_info' },
  { page: 'rules', icon: '📜', labelKey: 'nav_rules' },
  { page: 'info', icon: 'ℹ️', labelKey: 'nav_scoring' },
  { page: 'halloffame', icon: '🏆', labelKey: 'nav_hall_of_fame' },
  { page: 'events', icon: '📊', labelKey: 'nav_events' },
];

export default function Sidebar({ t, lang, setLang, activePage, onPageChange, clanHeader, discordInviteLink }) {
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div id="sidebar" className="hidden md:flex flex-col fixed md:sticky top-0 h-screen md:h-auto overflow-y-auto">
      {/* Clan Header */}
      <div
        className="sb-clan-header"
        onClick={() => onPageChange('dashboard')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onPageChange('dashboard')}
      >
        <div className="sb-badge-wrap">
          {clanHeader.badge
            ? <img id="clanLogo" src={sanitizeImageUrl(clanHeader.badge)} alt="Clan Badge" className="sb-badge-img" />
            : <span className="sb-badge-placeholder">⚔️</span>
          }
          <div className="sb-badge-ring" />
        </div>
        <div id="clanNameDisplay" className="sb-clan-name">{clanHeader.name || '—'}</div>
      </div>

      {/* Navigation */}
      <nav className="sb-nav" role="navigation" aria-label="Main navigation">
        {NAV_ITEMS.map(item => (
          <button
            key={item.page}
            onClick={() => onPageChange(item.page)}
            className={`sb-nav-btn${activePage === item.page ? ' sb-nav-btn-active' : ''}`}
            data-page={item.page}
            aria-current={activePage === item.page ? 'page' : undefined}
          >
            {activePage === item.page && <span className="sb-nav-pip" />}
            <span className="sb-nav-icon">{item.icon}</span>
            <span className="sb-nav-label">{t(item.labelKey)}</span>
          </button>
        ))}
      </nav>

      {/* Discord Section */}
      {discordInviteLink && (
        <div className="sb-discord-section">
          <a href={discordInviteLink} target="_blank" rel="noopener noreferrer" className="sb-discord-link">
            <DiscordIcon className="w-5 h-5 flex-shrink-0" />
            <span>{t('sidebar_discord_server')}</span>
          </a>
        </div>
      )}

      {/* Footer */}
      <div className="sb-footer">
        <div className="sb-footer-row">
          <div className="sb-home-tooltip-wrapper">
            <Link to="/" className="sb-home-btn" title={t('sidebar_back_home')}>
              <ArrowIcon className="sb-home-arrow" />
            </Link>
            <span className="sb-home-tooltip">{t('sidebar_back_home')}</span>
          </div>
          <div className="lp-lang-switcher sb-lang-dropdown" ref={langRef}>
            <button onClick={() => setLangOpen(o => !o)} className="lp-lang-btn compact">
              <span className="lp-lang-icon">🌐</span>
              <span className="lp-lang-label">{LANGS.find(l => l.code === lang)?.label || lang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <div className="lp-lang-menu" style={{ bottom: 'calc(100% + 0.4rem)', top: 'auto' }}>
                {LANGS.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`lp-lang-option${l.code === lang ? ' active' : ''}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
